import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { subDays } from 'date-fns';
import { compose, isNil } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, Section } from 'components';
import Panel from 'components/atoms/GefMui/Panel';
import { campaignTypes } from 'constants/campaigns';
import { codelists } from 'constants/codelists';
import PAGES from 'constants/pages';
import fetch from 'features/common/fetch';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import contactDetail from 'features/views/contactDetail';
import { equalOrThreeDashes, fromIsoDate, isDate, number, phone as phoneFormat, stringToDate } from 'utils';
import AdditionalData from 'pages/ContactDetail/components/AdditionalData';

import Client from './components/Client';
import InsuranceContract from './components/InsuranceContract';
import Notes from './components/Notes';
import Owner from './components/Owner';

export const PrintContactDetail = ({ idkRegionsCodelist, idkAgenciesCodelist, printList, contactItem }) => {
	const dispatch = useDispatch();
	const { contactId } = useParams();

	const contact = useSelector(contactDetail.selectors.getDetail);

	useEffect(() => {
		if (!printList) {
			dispatch(contactDetail.actions.getContactDetail(contactId));
			dispatch(contactDetail.actions.fetchContactAdditionalData(contactId));

			setTimeout(() => {
				handleOpenPrintWindow();
			}, 2500);
		}
		// eslint-disable-next-line
	}, [dispatch]);

	const handleOpenPrintWindow = () => {
		window.print();
	};

	const {
		additionalData = [],
		comments = [],
		solver = {},
		client = {},
		campaign = {},
		policy = {},
		contractEndDate,
		productName,
	} = printList ? contactItem : contact;

	const subheaderTitle = useRenderSubheaderTitle({
		pageTitle: `${getLabelMessage('printContactDetail:header')} (${campaign.fullName || contact.id})`,
		backButton: true,
	});

	const { campaignName, communicationName, openLetterDate, letterPrintedDate, type } = campaign;
	const { address = {} } = client;
	const { policyNo, insurancePremium } = policy;
	const owner = contactDetail.utils.getOwnerValue(
		solver,
		idkAgenciesCodelist,
		idkRegionsCodelist,
		'shortDescription',
	);
	const contactSource = `${campaignName || ''} - ${communicationName || ''}`;

	let letterSendDate = undefined;
	if (type === campaignTypes.X_SELL) {
		letterSendDate = openLetterDate;
	} else if (type !== campaignTypes.X_SELL && type !== undefined && letterPrintedDate) {
		letterSendDate = !isNilOrEmpty(contractEndDate) && subDays(new Date(contractEndDate), 5);
	}

	const streetNo = isNil(address.streetNo) ? '' : ` ${address.streetNo}`;
	const street = isNilOrEmpty(address.street) ? equalOrThreeDashes(address.street) : `${address.street}${streetNo}`;
	return (
		<Panel className screenId={PAGES.contactDetail.id} screenName={PAGES.contactDetail.name}>
			<Section>
				<Owner
					subheaderTitle={subheaderTitle}
					contactSource={contactSource}
					owner={equalOrThreeDashes(owner)}
					letterSend={isDate(letterSendDate) ? stringToDate(letterSendDate) : equalOrThreeDashes(undefined)}
					psEndDate={equalOrThreeDashes(fromIsoDate(contractEndDate))}
				/>
			</Section>
			<Section>
				<Client
					degree={equalOrThreeDashes(client.title)}
					name={equalOrThreeDashes(client.firstname)}
					surname={equalOrThreeDashes(client.surname)}
					dateNumber={equalOrThreeDashes(client.personIn)}
					businessIn={equalOrThreeDashes(client.businessIn)}
					businessName={equalOrThreeDashes(client.businessName)}
					street={equalOrThreeDashes(street)}
					town={equalOrThreeDashes(address.city)}
					zipCode={equalOrThreeDashes(address.zipCode)}
					phoneNumber={equalOrThreeDashes(phoneFormat(client.mobile))}
					phoneNumberTwo={equalOrThreeDashes(phoneFormat(client.phone))}
					email={equalOrThreeDashes(client.email)}
				/>
			</Section>
			<Section>
				<InsuranceContract
					policyNo={policyNo}
					product={equalOrThreeDashes(productName)}
					amount={equalOrThreeDashes(number(insurancePremium))}
				/>
			</Section>
			<Section>
				<AdditionalData
					additionalData={additionalData}
					fetchStorePath={`${fetch.constants.urls.contactDetail}.get`}
				/>
			</Section>
			<Section>
				<Notes comments={comments} />
			</Section>
		</Panel>
	);
};

PrintContactDetail.propTypes = {
	contactItem: PropTypes.object,
	idkAgenciesCodelist: PropTypes.array,
	idkRegionsCodelist: PropTypes.array,
	printList: PropTypes.bool,
};

export default compose(
	codelistProvider(codelists.idkRegions, 'idkRegionsCodelist'),
	codelistProvider(codelists.idkAgencies, 'idkAgenciesCodelist'),
)(PrintContactDetail);
